import React, { useEffect, useMemo, useRef, useState } from "react";

import { MapContainer, TileLayer, Marker, Popup, Polygon, Map, Tooltip } from "react-leaflet";
import { useLists } from "../contexts/Lists";
import { getTitleFromThreartDrillKey } from "../utils/utils";
import { getDateTimeString } from "../utils/date";
import lefleat from "leaflet";

const getMemoCacheData = (data) => {
  const counter = {};
  data.forEach(({ cities, threat, date }) => {
    cities.forEach(({ name, id, areaName }) => {
      if (counter[id]) {
        counter[id].count++;
        if (counter[id].countPerThreat[threat]) counter[id].countPerThreat[threat]++;
        else counter[id].countPerThreat[threat] = 1;
      } else counter[id] = { name, id, areaName, count: 1, countPerThreat: { [threat]: 1 }, date };

      if (date.getTime() > counter[id].date?.getTime()) counter[id].date = date;
    });
  });

  const counts = Object.values(counter).map((p) => p.count);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts);

  return { counter, counts, minCount, maxCount };
};

const Map1 = ({ data }) => {
  const { cities, polygons } = useLists();

  const [map, setMap] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setPopupOpenFor(null);
    resetBounds();
  }, [data, isReady]);

  const [popupOpenFor, setPopupOpenFor] = useState(null);

  const { counter, minCount, maxCount } = useMemo(() => getMemoCacheData(data), [data]);

  // Helper function to calculate color using a logarithmic scale
  function calculateColor(count) {
    // Apply a logarithmic scale
    const logMin = Math.log(minCount + 1);
    const logMax = Math.log(maxCount + 1);
    const logCount = Math.log(count + 1);

    // Normalize log-scaled count between 0 and 1
    const normalizedLogCount = (logCount - logMin) / (logMax - logMin || 1);

    // Calculate color based on normalized log count
    const red = Math.round(255 * normalizedLogCount);
    const green = Math.round(255 * (1 - normalizedLogCount));
    return `rgb(${red}, ${green}, 0)`;
  }

  const resetBounds = () => {
    if (!isReady || Object.keys(polygons).length === 0) return;
    const values = Object.values(counter);
    if (values.length === 0) return;
    const bounds = lefleat.latLngBounds(null, null);

    let noPointsExtended = true;
    Object.values(counter).forEach(({ id }) => {
      const polygon = polygons[id];
      if (!polygon?.length) return;
      polygon.forEach((point) => {
        bounds.extend(point);
        noPointsExtended = false;
      });
    });

    try {
      if (!noPointsExtended)
        map.flyToBounds(bounds, {
          duration: 0.5,
          //   padding: [1, 1],
        });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <MapContainer whenReady={() => setIsReady(true)} ref={setMap} center={[31.5, 34.9]} zoom={8} scrollWheelZoom={true}>
      <TileLayer
        key="Google Maps"
        url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=iw"
        subdomains={["mt0", "mt1", "mt2", "mt3"]}
        maxZoom={20}
        attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
      />
      {popupOpenFor && (
        <Popup position={popupOpenFor.point}>
          <span className="underline">
            {" "}
            {popupOpenFor.city.areaName}: <b>{popupOpenFor.city.name} </b> <br />
          </span>
          {Object.entries(popupOpenFor.city.countPerThreat).map(([threat, val]) => (
            <div>
              <b> {getTitleFromThreartDrillKey(Number(threat), false)}</b>: {val}
            </div>
          ))}
          {Object.keys(popupOpenFor.city.countPerThreat).length !== 1 && (
            <b>
              סה"כ: {popupOpenFor.city.count} <br />
            </b>
          )}
          (אחרונה ב {getDateTimeString(popupOpenFor.city.date, true)})
        </Popup>
      )}

      {Object.keys(polygons).length !== 0 &&
        Object.values(counter).map(
          (city) =>
            polygons[city.id] && (
              <Polygon
                key={city.id}
                eventHandlers={{ click: ({ latlng }) => setPopupOpenFor({ point: [latlng.lat, latlng.lng], city }) }}
                opacity={55}
                weight={1.8}
                pathOptions={{
                  color: calculateColor(city.count, maxCount),
                  fillColor: calculateColor(city.count, maxCount),
                  fillOpacity: 0.5,
                  opacity: 2,
                }}
                positions={polygons[city.id]}
              ></Polygon>
            )
        )}
    </MapContainer>
  );
};

export default Map1;
